import { YapMoney } from '@/types/general';
import { useProfileStore } from '@/store/profileStore';
import { factoredYapAmount, stringToYapAmount, YapAmount } from '@/utils/general';
import { IncoreQuote } from '@/components/payments/types/incoreQuote';

const transactionsLocaleDateOption: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: '2-digit',
  year: 'numeric'
};

const localeDateShortOption: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric'
};

const transactionsLocaleTimeOption: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric',
  hour12: false
};

const localeDateTimeOption: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: '2-digit',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: false
};

const transactionsMonthLongOption: Intl.DateTimeFormatOptions = {
  month: 'long'
};

export const supportedLocales = ['de_CH', 'fr_CH', 'it_CH', 'en_CH'];

export enum SupportedLanguages {
  DE = 'de',
  FR = 'fr',
  IT = 'it',
  EN = 'en'
}

export enum LocaleFormatType {
  Time = 'yapTime',
  Date = 'yapDate',
  DateShort = 'yapDateShort',
  DateTime = 'yapDateTime'
}

export const formatIsoToLocaleString = (isoDate: string, type?: LocaleFormatType): string => {
  let option: Intl.DateTimeFormatOptions;
  if (type === LocaleFormatType.Time) {
    option = transactionsLocaleTimeOption;
  } else if (type === LocaleFormatType.Date) {
    option = transactionsLocaleDateOption;
  } else if (type === LocaleFormatType.DateShort) {
    option = localeDateShortOption;
  } else if (type === LocaleFormatType.DateTime) {
    option = localeDateTimeOption;
  } else {
    option = transactionsLocaleDateOption;
  }
  return new Date(isoDate).toLocaleString(getPreferredLocale(), option);
};

export const getMonthFromIsoString = (isoDate: string): string => {
  return new Date(isoDate).toLocaleString(getPreferredLocale(), transactionsMonthLongOption);
};

export const formatNumber = (value: string | number, maxDecimals?: number) => {
  const num = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat(getPreferredLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: maxDecimals ?? 2
  }).format(num);
};

export interface FormatAmountOptions {
  showPositiveSign?: boolean;
  showNegativeSign?: boolean;
  hidden?: boolean;
  ccyClasses?: string;
  lineThrough?: boolean;
}
/**
 * Format the given amount to the local number format with currency.
 * By default, positive amounts are prefixed with a "+" while negative ones drop the "-".
 * You can change this behaviour by playing with `showPositiveSing` & `showNegativeSign`
 *
 * @param amount  the amount to format
 * @param currency  the currency defaults to CHF
 * @param showPositiveSign  whether or not to prefix positive amount with "+"
 * @param showNegativeSign  whether ot not to show the negative sing
 * @param hidden  whether the amount should be displayed or replaced by "***"
 * @param ccyClasses  classes to style of the currency span "***"
 * @returns string  the formatted amount
 */
export function formatAmount(
  amount: number | string | undefined,
  currency?: string,
  showPositiveSign = true,
  showNegativeSign = false,
  hidden = false,
  ccyClasses?: string,
  lineThrough = false
): string {
  const yapAmount = stringToYapAmount(`${amount}`);
  return formatYapAmount(yapAmount, currency ?? 'CHF', {
    showNegativeSign,
    showPositiveSign,
    hidden,
    ccyClasses,
    lineThrough
  });
}

/**
 * Format the given YapMoney to the local number format with currency.
 * By default, positive amounts are prefixed with a "+" while negative ones drop the "-".
 * You can change this behaviour by playing with `showPositiveSing` & `showNegativeSign`
 *
 * @param amount  the amount to format
 * @param currency  the currency
 * @param options  the options to format the amount
 * @returns string  the formatted amount
 */
export function formatYapMoney(amount: YapMoney, options?: FormatAmountOptions): string {
  return formatYapAmount(amount.amount, amount.currency, options);
}

/**
 * Format the given YapAmount to the local number format with currency (if provided).
 * By default, positive amounts are prefixed with a "+" while negative ones drop the "-".
 * You can change this behaviour by playing with `showPositiveSing` & `showNegativeSign`
 *
 * @param amount  the amount to format
 * @param currency  the currency
 * @param options  the options to format the amount
 * @returns string  the formatted amount
 */
export function formatYapAmount(amount: YapAmount, currency?: string, options?: FormatAmountOptions): string {
  if (amount === undefined) return '';
  // const numberAmount =  typeof amount === 'string' ? Number(amount) : amount;
  const numberAmount = factoredYapAmount(amount, true) as number;

  let formatted = '***';
  if (!options?.hidden) {
    formatted = new Intl.NumberFormat(getPreferredLocale(), {
      minimumFractionDigits: 2
    }).format(options?.showNegativeSign ? numberAmount : Math.abs(numberAmount));
  }
  const ccy = currency ? currency : '';
  formatted = numberAmount > 0 && options?.showPositiveSign ? `+ ${formatted}` : formatted;

  const lineThroughClass = options?.lineThrough ? 'line-through' : '';

  if (options?.ccyClasses) {
    formatted = `<span class="text-sm ${options?.ccyClasses} ${lineThroughClass}">${ccy}</span> <span class="${lineThroughClass}">${formatted}</span>`;
  } else {
    formatted = `<span class="text-yapgray-500 text-sm ${lineThroughClass}">${ccy}</span> <span class="${lineThroughClass}">${formatted}</span>`;
  }

  return formatted;
}

export function formatFxRate(incoreQuote: IncoreQuote): string {
  const factored = factoredYapAmount(incoreQuote.exchangeRateDetails.target.amount, true) as number;
  const formatted = factored.toPrecision(4);

  const target = incoreQuote.targetAmount.currency;
  const source = incoreQuote.sourceAmount.currency;

  return `1 ${source} = ${formatted} ${target}`;
}

export function getFirstBestSupportedLocale(): string {
  let bestMatch = 'en';
  for (const lang of navigator.languages) {
    const shortLang = lang.split('-')[0];
    if (supportedLocales.includes(shortLang)) {
      bestMatch = shortLang;
      break;
    }
  }

  return bestMatch;
}

/**
 * Retrieve the preferred locale from the user profile if available, otherwise will take the best match from the browser.
 * @returns string
 */
export function getPreferredLocale(): string {
  const profileStore = useProfileStore();
  return (profileStore.communication.locale || getFirstBestSupportedLocale()).replace('_', '-');
}

/**
 * Parse the preferredLocale and extract the language out of it.
 * locale: fr-CH ==> returns fr
 * @returns string
 */
export function getPreferredLanguage(): SupportedLanguages {
  // @ts-ignore
  return SupportedLanguages[getPreferredLocale().substring(0, 2).toUpperCase()];
}
